import { ref, computed, type Ref, type ComputedRef } from "vue";
import type { ComboboxContentProps } from "./combobox.types";
import { useComponentContext } from "composables/useComponentContext";
import { useCombobox, getComboboxInputElement } from "./useCombobox";
import { resolvePositioning } from "composables/usePosition";
import { resolveRenderedElement } from "@/runtime/shared/shadow-dom";
import type { AcceptableValue } from "@/runtime/types/utils";

export interface UseComboboxContentOptions {
  props: Readonly<ComboboxContentProps>;
}

export interface UseComboboxContentReturn {
  inputId: Ref<string>;
  inputWidth: Ref<number>;
  listboxStyle: ComputedRef<{ minWidth: string; maxHeight: string }>;
  positioning: ComputedRef<{
    placement: string;
    offset: number;
    boundaryMargin: number;
    collisionStrategy: string;
  }>;
  combobox: ReturnType<typeof useCombobox>;
  handleFloatingClose: () => void;
  handleFloatingOpen: () => void;
}

/** Locate the input element via registry and generate an input ID. */
const resolveInput = (
  comboboxElement: Element | null,
  uid: number | undefined,
): { inputIdValue: string; inputElement: HTMLElement | null } => {
  let inputIdValue = `e-combobox-input-${uid}`;
  let inputElement: HTMLElement | null = null;

  if (comboboxElement) {
    const input = getComboboxInputElement(comboboxElement) as HTMLElement | null;
    if (input) {
      inputElement = input;
      if (comboboxElement.id) inputIdValue = `${comboboxElement.id}-input`;
      input.id = inputIdValue;
    }
  }

  return { inputIdValue, inputElement };
};

/** Resolve which value to highlight when the floating panel opens. */
const resolveOpenHighlight = (combobox: ReturnType<typeof useCombobox>): AcceptableValue | undefined => {
  const currentHighlighted = combobox.highlightedValue.value;
  if (currentHighlighted !== undefined) return currentHighlighted;

  const currentValue = combobox.modelValue.value;
  const filteredItems = combobox.filteredItems.value;

  if (currentValue !== undefined) {
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      return currentValue.find((v) => filteredItems.includes(v));
    }
    if (!Array.isArray(currentValue) && filteredItems.includes(currentValue)) {
      return currentValue;
    }
  }

  return filteredItems.length > 0 ? filteredItems[0] : undefined;
};

export const useComboboxContent = (options: UseComboboxContentOptions): UseComboboxContentReturn => {
  const { props } = options;
  const combobox = useCombobox();
  const context = useComponentContext();

  const { inputIdValue, inputElement } = resolveInput(combobox.comboboxElement.value, context.uid ?? undefined);
  const inputId = ref(inputIdValue);
  const inputWidth = ref(inputElement ? resolveRenderedElement(inputElement).offsetWidth : 160);

  const listboxStyle = computed(() => ({
    minWidth: `${inputWidth.value}px`,
    maxHeight: props.maxHeight ?? "300px",
  }));
  const positioning = computed(() => resolvePositioning(combobox.comboboxProps.value?.positioning, props));

  const handleFloatingClose = () => {
    combobox.setHighlightedValue(undefined);
    combobox.setFocusValue(undefined);
    (inputElement?.shadowRoot?.querySelector("input") as HTMLElement | null)?.focus();
    combobox.close();
  };

  const handleFloatingOpen = () => {
    if (inputElement) {
      inputWidth.value = resolveRenderedElement(inputElement).offsetWidth;
      (inputElement.shadowRoot?.querySelector("input") as HTMLElement | null)?.focus();
    }
    const valueToFocus = resolveOpenHighlight(combobox);
    if (valueToFocus !== undefined) {
      combobox.setHighlightedValue(valueToFocus);
      combobox.setFocusValue(valueToFocus);
      requestAnimationFrame(() => combobox.scrollItemIntoView(valueToFocus));
    }
  };

  return { inputId, inputWidth, listboxStyle, positioning, combobox, handleFloatingClose, handleFloatingOpen };
};
